import { AuthLayout } from '@/components/auth/auth-layout.component';
import { AuthPreviewCarousel } from '@/components/auth/auth-preview-carousel.component';
import { useAuthRedirect } from '@/hooks/use-auth-redirect.hook';
import { peekPostAuthRedirect } from '@/lib/post-auth-redirect';
import { useSignupsPaused } from '@/hooks/use-signups-paused.hook';
import { useTheme } from '@/hooks/use-theme.hook';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SignIn, useSignIn } from '@clerk/react';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import '../../../src/index.css';
import { BRAND } from '@/lib/brand';
import { AppIcon } from '@/components/ui/app-icon';
import { iconKey } from '@/lib/icons';

export const Route = createFileRoute('/auth/login')({
  component: SignInPage,
});

function SignInPage() {
  useAuthRedirect();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const signupsPaused = useSignupsPaused();
  const ssoHost = useSocialButtonsSlot();

  return (
    <AuthLayout
      leftPanel={
        <>
          <div className='mb-8 text-center'>
            <h1 className='text-2xl font-semibold text-foreground'>
              Welcome back to {BRAND.name}
            </h1>
            <p className='mt-2 text-sm text-muted-foreground'>
              Login to continue
            </p>
          </div>

          <SignIn
            fallbackRedirectUrl={peekPostAuthRedirect() ?? '/'}
            signUpUrl='/auth/register'
            // Stops "Continue with Google" from silently creating an account
            // for an unknown email while sign-ups are paused.
            transferable={!signupsPaused}
            appearance={{
              elements: {
                rootBox: 'mx-auto w-full max-w-full',
                cardBox:
                  'bg-white dark:bg-[#1a1a1a] shadow-xs dark:shadow-none border border-gray-100 dark:border-white/6 w-full max-w-full',
                card: 'bg-white dark:bg-[#1a1a1a] shadow-none border-0 w-full max-w-full',
                headerTitle: 'text-gray-900 dark:text-white',
                headerSubtitle: 'text-gray-500 dark:text-gray-400',
                socialButtonsBlockButton:
                  'rounded-full! bg-white border-gray-100 hover:bg-gray-50 text-gray-700 dark:bg-[#1a1a1a] dark:border-white/6 dark:hover:bg-white/[0.03] dark:text-gray-300',
                formFieldLabel: 'text-gray-700 dark:text-gray-300',
                formFieldInput:
                  'bg-white border-gray-100 text-gray-900 placeholder:text-gray-400 dark:bg-[#1a1a1a] dark:border-white/6 dark:text-white dark:placeholder:text-gray-500',
                // Flat pill CTA to match the redesign: bg-primary keeps the
                // solid Lemlist fill, but !bg-none/!shadow-none suppress the
                // global button.bg-primary 3D gradient+glow (index.css) so it
                // reads flat, and rounded-full! makes it a pill.
                formButtonPrimary:
                  'bg-primary text-primary-foreground rounded-full! bg-none! shadow-none! font-semibold',
                footer:
                  'bg-white text-gray-500 dark:bg-[#1a1a1a] dark:text-gray-400',
                // hidden! (important) to beat Clerk's unlayered styles.
                footerAction: signupsPaused
                  ? 'hidden!'
                  : 'bg-white dark:bg-[#1a1a1a]',
                footerActionLink: 'text-primary hover:text-primary/80',
                footerActionText: 'text-gray-500 dark:text-gray-400',
                footerPages: 'bg-white dark:bg-[#1a1a1a]',
                footerPageLink:
                  'bg-white text-gray-500 dark:bg-[#1a1a1a] dark:text-gray-400',
                identityPreviewText: 'text-gray-700 dark:text-gray-300',
                identityPreviewEditButtonIcon:
                  'text-gray-500 dark:text-gray-400',
                // "Last used" pill on the previously-used social provider
                // button. Clerk renders this as `cl-lastAuthenticationStrategyBadge`
                // (NOT the generic `cl-badge`), with default border + text in
                // `colorNeutral` at low opacity - invisible on our dark card.
                lastAuthenticationStrategyBadge:
                  'bg-gray-100! text-gray-700! border! border-gray-200! ' +
                  'dark:!bg-white/[0.08] dark:text-gray-200! dark:border-white/16!',
                formFieldInputShowPasswordButton:
                  'text-gray-500 dark:text-gray-400',
                otpCodeFieldInput:
                  'bg-white border-gray-100 text-gray-900 dark:bg-[#1a1a1a] dark:border-white/6 dark:text-white',
                formResendCodeLink: 'text-primary hover:text-primary/80',
                logoBox:
                  'bg-white text-gray-500 dark:bg-[#1a1a1a] dark:text-gray-400',
                logoImage: 'brightness-100 dark:brightness-90',
              },
              variables: {
                // Hex equivalent of HSL(222, 100%, 60%) - the current
                // --primary token in index.css. Clerk's variables don't
                // accept HSL/CSS-vars, so we mirror the token's hex value
                // here and update if --primary shifts.
                colorPrimary: '#316bff',
                colorDanger: '#ef4444',
                colorSuccess: '#10b981',
                colorWarning: '#f59e0b',
                colorNeutral: '#6b7280',
                colorBackground: isDark ? '#1a1a1a' : '#ffffff',
                colorForeground: isDark ? '#ffffff' : '#111827',
                colorMutedForeground: isDark ? '#9ca3af' : '#6b7280',
              },
            }}
          />
          {ssoHost && createPortal(<SsoSignIn />, ssoHost)}
        </>
      }
      rightPanel={<AuthPreviewCarousel variant='login' />}
    />
  );
}

// Clerk's <SignIn> has no slot under the Google/Microsoft buttons, so we add
// a div at the end of its social-buttons block and portal the SSO button into
// it. The observer re-adds it whenever Clerk re-renders that step.
function useSocialButtonsSlot() {
  const [host, setHost] = useState<HTMLElement | null>(null);
  const hostRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const attach = () => {
      const root = document.querySelector('.cl-socialButtonsRoot');
      if (!root) return;
      if (hostRef.current?.parentElement === root) return;
      const div = document.createElement('div');
      div.className = 'mt-2';
      root.appendChild(div);
      hostRef.current = div;
      setHost(div);
    };
    attach();
    const observer = new MutationObserver(attach);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return host;
}

// Enterprise SSO (SAML connections set up per company domain in Clerk).
// Clerk sends the email to the IdP that owns its domain, or errors if no
// connection matches.
function SsoSignIn() {
  const { signIn } = useSignIn();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  if (!open) {
    return (
      <button
        type='button'
        onClick={() => setOpen(true)}
        className='flex h-8 w-full items-center justify-center gap-2 rounded-full border border-gray-200 bg-white text-[13px] font-medium text-gray-700 hover:bg-gray-50 dark:border-white/10 dark:bg-[#1a1a1a] dark:text-gray-300 dark:hover:bg-white/[0.03]'
      >
        <AppIcon icon={iconKey} style={{ fontSize: 14 }} />
        Sign in with SSO
      </button>
    );
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setPending(true);
    setError(null);
    const { error } = await signIn.sso({
      strategy: 'enterprise_sso',
      identifier: email.trim(),
      redirectUrl: peekPostAuthRedirect() ?? '/',
      redirectCallbackUrl: '/auth/sso-callback',
    });
    // On success the browser is already leaving for the IdP.
    // Clerk answers an unknown domain with "Couldn't find your account",
    // which reads wrong here, so show one clear message instead.
    if (error) {
      setError(
        "SSO isn't set up for this email's domain. Use Google, Microsoft or email instead."
      );
      setPending(false);
    }
  };

  return (
    <form onSubmit={submit} className='space-y-2'>
      <Input
        type='email'
        required
        autoFocus
        autoComplete='email'
        placeholder='you@company.com'
        aria-label='Work email'
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      {error && <p className='text-sm text-destructive'>{error}</p>}
      <Button type='submit' className='w-full rounded-full' disabled={pending}>
        {pending ? 'Redirecting…' : 'Continue with SSO'}
      </Button>
      <button
        type='button'
        onClick={() => setOpen(false)}
        className='mx-auto block text-xs text-muted-foreground hover:text-foreground'
      >
        Back
      </button>
    </form>
  );
}

export default SignInPage;
